import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { iif, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Task } from './../../services/dtos/task';
import { TaskBasicInfo } from './../../services/dtos/taskBasicInfo';
import { TasksService } from './../../services/tasks.service';
import {
  ClearCurrentTaskId,
  DeleteTask,
  FetchTask,
  FetchTaskContent,
  FetchTasksBasicInfo,
  ScheduleTaskForReRun,
  SetCurrentTaskId,
} from './tasks.actions';
import { TaskInfoResponse } from 'src/app/services/dtos/taskInfoResponse';
import { TaskContent } from 'src/app/services/dtos/taskContent';

export class TasksStateModel {
  public selectedTaskId!: string;
  public tasks!: Task[];
  public currentProcessTypeContent!: TaskContent;
  public errorMessage!: string;
}

const defaults = {
  selectedTaskId: '',
  tasks: [],
  currentProcessTypeContent: {taskId: '', content: '', extension: ''},
  errorMessage: '',
};

@State<TasksStateModel>({
  name: 'tasksState',
  defaults
})

@Injectable()
export class TasksState {
  public constructor(private tasksService: TasksService) { }

  @Selector()
  public static getSelectedTaskId(state: TasksStateModel): string {
    return state.selectedTaskId;
  }

  @Selector()
  public static getSelectedTask(state: TasksStateModel): Task | undefined {
    return state.tasks.find(c => c.id === state.selectedTaskId);
  }

  @Selector()
  public static getTasksBasicInfo(state: TasksStateModel): TaskBasicInfo[] {
    return state.tasks as TaskBasicInfo[];
  }

  @Selector()
  public static getCurrentProcessTypeContent(state: TasksStateModel): TaskContent {
    return state.currentProcessTypeContent as TaskContent;
  }

  @Selector()
  public static getErrorMessage(state: TasksStateModel): string {
    return state.errorMessage;
  }

  @Action(FetchTask)
  public fetchSingle({ setState }: StateContext<TasksStateModel>, { id }: FetchTask): Observable<Task> {
    return this.tasksService.getTask(id)
      .pipe(
        tap((task: Task) => {
          setState(
            patch<TasksStateModel>({
              tasks: iif<Task[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                tasks => tasks!.some(x => x.id === task.id),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                updateItem<Task>(x => x!.id === task.id, task),
                insertItem<Task>(task)
              )
            })
          );
        })
      );
  }

  @Action(FetchTaskContent)
  public fetchTaskContent(
    { setState }: StateContext<TasksStateModel>,
    { id, processType }: FetchTaskContent,
  ): Observable<TaskContent> {
    return this.tasksService.getTaskContent(id, processType).pipe(
      tap((taskContent: TaskContent) => {
        setState(
          patch({
            currentProcessTypeContent: taskContent,
          }),
        );
      }),
    );
  }

  @Action(FetchTasksBasicInfo)
  public fetchAll({ getState, setState, dispatch }:
    StateContext<TasksStateModel>, { from, to, filters }: FetchTasksBasicInfo): Observable<TaskInfoResponse> {
    return this.tasksService.getAllBasicInfo(from, to, filters)
      .pipe(
        tap((freshTasksInfo: TaskInfoResponse) => {
          const state = getState();
          setState(
            patch<TasksStateModel>({
              tasks: freshTasksInfo.tasks,
              errorMessage: freshTasksInfo.errorMessage
            })
          );

          if (!freshTasksInfo.tasks || freshTasksInfo.tasks.length === 0 || 
            !freshTasksInfo.tasks.some((task) => task.id === state.selectedTaskId)) {
            setState(
              patch({
                selectedTaskId: ''
              })
            );
          } else if (state.selectedTaskId) {
            dispatch(new FetchTask(state.selectedTaskId));
          }
        })
      );
  }

  @Action(ScheduleTaskForReRun)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public scheduleForReRun({ dispatch }: StateContext<TasksStateModel>, { payload }: ScheduleTaskForReRun): Observable<any> {
    return this.tasksService.scheduleTaskForReRun(payload)
      .pipe(
        tap(() => {
          dispatch(new FetchTask(payload.id));
        })
      );
  }

  @Action(DeleteTask)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public delete({ setState }: StateContext<TasksStateModel>, { id }: DeleteTask): Observable<any> {
    return this.tasksService.deleteTask(id)
      .pipe(
        tap(() => {
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              tasks: removeItem<Task>(x => x!.id === id),
              selectedTaskId: ''
            })
          );
        })
      );
  }

  @Action(SetCurrentTaskId)
  public setCurrentTaskId({ setState }: StateContext<TasksStateModel>, { id }: SetCurrentTaskId): void {
    setState(
      patch({
        selectedTaskId: id
      })
    );
  }

  @Action(ClearCurrentTaskId)
  public clearCurrentTaskId({ setState }: StateContext<TasksStateModel>): void {
    setState(
      patch({
        selectedTaskId: ''
      })
    );
  }
}
